import { db } from "../config/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";

// Initialize Stripe - COMMENTED OUT FOR BUILD/DEPLOY
// Uncomment when REACT_APP_STRIPE_PUBLISHABLE_KEY is configured in .env
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const stripePromise = null;

class PaymentService {
  constructor() {
    this.paymentsRef = collection(db, "payments");
    // BKT Bank Virtual POS configuration
    this.bktConfig = {
      apiUrl:
        process.env.REACT_APP_BKT_API_URL || "https://bkt-virtual-pos-api-url",
      merchantId: process.env.REACT_APP_BKT_MERCHANT_ID || "",
      terminalId: process.env.REACT_APP_BKT_TERMINAL_ID || "",
      successUrl: `${window.location.origin}/booking-success`,
      failureUrl: `${window.location.origin}/booking-failure`,
      cancelUrl: `${window.location.origin}/confirm-booking`,
    };
  }

  /**
   * Create BKT Bank Virtual POS Payment Session
   */
  async createBKTPayment(bookingData) {
    try {
      const {
        bookingId,
        amount,
        currency = "ALL", // Albanian Lek (BKT's default currency)
        description,
        bookingDetails = {},
      } = bookingData;

      // Validate required fields
      if (!amount || amount <= 0) {
        throw new Error("Invalid payment amount");
      }

      if (!this.bktConfig.merchantId || !this.bktConfig.terminalId) {
        throw new Error(
          "BKT Bank credentials not configured. Please add REACT_APP_BKT_MERCHANT_ID and REACT_APP_BKT_TERMINAL_ID to your .env file",
        );
      }

      // Create payment record in Firebase first
      const paymentRecord = {
        bookingId,
        amount,
        currency,
        paymentMethod: "bkt_virtual_pos",
        status: "pending",
        description: description || `Booking #${bookingId}`,
        bookingDetails: {
          date: bookingDetails.date,
          timeSlot: bookingDetails.timeSlot,
          numberOfGuests: bookingDetails.numberOfGuests,
          customerInfo: bookingDetails.customerInfo,
          extras: bookingDetails.extras,
        },
        metadata: {
          source: "web",
          platform: "SeaPulse Albania",
          userAgent: navigator.userAgent,
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const paymentDoc = await addDoc(this.paymentsRef, paymentRecord);
      const paymentId = paymentDoc.id;

      console.log("BKT payment record created in Firebase:", paymentId);

      // Prepare BKT Virtual POS payment request
      const bktPaymentRequest = {
        merchantId: this.bktConfig.merchantId,
        terminalId: this.bktConfig.terminalId,
        amount: amount.toFixed(2),
        currency: currency,
        orderId: paymentId, // Use Firebase payment ID as order ID
        description: description || `SeaPulse Albania - Booking`,
        successUrl: this.bktConfig.successUrl,
        failureUrl: this.bktConfig.failureUrl,
        cancelUrl: this.bktConfig.cancelUrl,
        // Customer information
        customerEmail: bookingDetails.customerInfo?.email || "",
        customerName: bookingDetails.customerInfo?.fullName || "",
        customerPhone: bookingDetails.customerInfo?.phone || "",
      };

      // Call BKT Bank API to create payment session
      // TODO: Replace with actual BKT Bank API endpoint and authentication
      const response = await fetch(`${this.bktConfig.apiUrl}/payment/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add BKT Bank API authentication headers here
          // "Authorization": `Bearer ${your_bkt_api_key}`,
        },
        body: JSON.stringify(bktPaymentRequest),
      });

      if (!response.ok) {
        throw new Error(
          `BKT API error: ${response.status} ${response.statusText}`,
        );
      }

      const bktResponse = await response.json();

      // Update payment record with BKT session information
      await updateDoc(doc(this.paymentsRef, paymentId), {
        bktSessionId: bktResponse.sessionId || bktResponse.transactionId,
        bktPaymentUrl: bktResponse.paymentUrl || bktResponse.redirectUrl,
        updatedAt: Timestamp.now(),
      });

      console.log("BKT payment session created:", bktResponse);

      return {
        success: true,
        paymentId: paymentId,
        paymentUrl: bktResponse.paymentUrl || bktResponse.redirectUrl,
        sessionId: bktResponse.sessionId || bktResponse.transactionId,
        message: "BKT payment session created successfully",
      };
    } catch (error) {
      console.error("Error creating BKT payment:", error);
      throw new Error(`Failed to create BKT payment: ${error.message}`);
    }
  }

  /**
   * Verify BKT Bank Payment Status
   */
  async verifyBKTPayment(paymentId, transactionData = {}) {
    try {
      if (!paymentId) {
        throw new Error("Missing payment ID");
      }

      const paymentRef = doc(this.paymentsRef, paymentId);

      // Call BKT Bank API to verify payment status
      // TODO: Replace with actual BKT Bank verification endpoint
      const verificationRequest = {
        merchantId: this.bktConfig.merchantId,
        terminalId: this.bktConfig.terminalId,
        orderId: paymentId,
        transactionId:
          transactionData.transactionId || transactionData.sessionId,
      };

      const response = await fetch(`${this.bktConfig.apiUrl}/payment/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add BKT Bank API authentication headers here
        },
        body: JSON.stringify(verificationRequest),
      });

      if (!response.ok) {
        throw new Error(`BKT verification failed: ${response.status}`);
      }

      const verificationResult = await response.json();

      // Update payment record based on verification result
      const updateData = {
        bktTransactionId: verificationResult.transactionId,
        bktAuthCode: verificationResult.authCode,
        bktReferenceNumber: verificationResult.referenceNumber,
        paymentStatus: verificationResult.status,
        status:
          verificationResult.status === "APPROVED" ||
          verificationResult.status === "SUCCESS"
            ? "succeeded"
            : "failed",
        updatedAt: Timestamp.now(),
      };

      if (updateData.status === "succeeded") {
        updateData.completedAt = Timestamp.now();
      }

      await updateDoc(paymentRef, updateData);

      console.log("BKT payment verified:", paymentId, verificationResult);

      return {
        success: updateData.status === "succeeded",
        paymentId,
        status: verificationResult.status,
        transactionId: verificationResult.transactionId,
        message:
          updateData.status === "succeeded"
            ? "Payment successful"
            : "Payment failed",
      };
    } catch (error) {
      console.error("Error verifying BKT payment:", error);

      // Mark payment as failed
      try {
        const paymentRef = doc(this.paymentsRef, paymentId);
        await updateDoc(paymentRef, {
          status: "failed",
          error: error.message,
          updatedAt: Timestamp.now(),
        });
      } catch (updateError) {
        console.error("Error updating failed payment status:", updateError);
      }

      throw new Error(`Failed to verify BKT payment: ${error.message}`);
    }
  }

  /**
   * Update BKT Payment Status (for webhook/callback handling)
   */
  async updateBKTPaymentStatus(paymentId, statusData) {
    try {
      const paymentRef = doc(this.paymentsRef, paymentId);

      const updateData = {
        bktTransactionId: statusData.transactionId,
        bktAuthCode: statusData.authCode,
        bktReferenceNumber: statusData.referenceNumber,
        paymentStatus: statusData.status,
        status:
          statusData.status === "APPROVED" || statusData.status === "SUCCESS"
            ? "succeeded"
            : "failed",
        updatedAt: Timestamp.now(),
      };

      if (updateData.status === "succeeded") {
        updateData.completedAt = Timestamp.now();
      }

      await updateDoc(paymentRef, updateData);

      return {
        success: true,
        paymentId,
        status: updateData.status,
      };
    } catch (error) {
      console.error("Error updating BKT payment status:", error);
      throw error;
    }
  }

  /**
   * Create PayPal Order (Legacy - kept for reference)
   */
  async createPayPalOrder(bookingData) {
    try {
      const {
        bookingId,
        amount,
        currency = "EUR",
        description,
        bookingDetails = {},
      } = bookingData;

      // Validate required fields
      if (!amount || amount <= 0) {
        throw new Error("Invalid payment amount");
      }

      // Create comprehensive payment record in Firebase
      const paymentRecord = {
        bookingId,
        amount,
        currency,
        paymentMethod: "paypal",
        status: "pending",
        description,
        bookingDetails: {
          date: bookingDetails.date,
          timeSlot: bookingDetails.timeSlot,
          numberOfGuests: bookingDetails.numberOfGuests,
          customerInfo: bookingDetails.customerInfo,
          extras: bookingDetails.extras,
        },
        metadata: {
          source: "web",
          platform: "SeaPulse Albania",
          userAgent: navigator.userAgent,
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const paymentDoc = await addDoc(this.paymentsRef, paymentRecord);

      console.log("PayPal order created in Firebase:", paymentDoc.id);

      return {
        paymentId: paymentDoc.id,
        message: "PayPal order created successfully",
      };
    } catch (error) {
      console.error("Error creating PayPal order:", error);
      throw new Error(`Failed to create payment: ${error.message}`);
    }
  }

  /**
   * Confirm PayPal Payment
   */
  async confirmPayPalPayment(paymentId, paypalOrderId, payerInfo) {
    try {
      if (!paymentId || !paypalOrderId) {
        throw new Error("Missing payment or order ID");
      }

      const paymentRef = doc(this.paymentsRef, paymentId);

      // Update payment record with complete PayPal transaction details
      await updateDoc(paymentRef, {
        paypalOrderId,
        paypalTransactionId: payerInfo.captureId,
        payerInfo: {
          payerId: payerInfo.payerId,
          email: payerInfo.email,
          name: payerInfo.name,
          country: payerInfo.country,
        },
        paymentStatus: payerInfo.status,
        amount: parseFloat(payerInfo.amount),
        currency: payerInfo.currency,
        status: "succeeded",
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      console.log("PayPal payment confirmed:", paymentId);

      return {
        success: true,
        paymentId,
        message: "Payment confirmed successfully",
      };
    } catch (error) {
      console.error("Error confirming PayPal payment:", error);

      // Try to mark payment as failed
      try {
        const paymentRef = doc(this.paymentsRef, paymentId);
        await updateDoc(paymentRef, {
          status: "failed",
          error: error.message,
          updatedAt: Timestamp.now(),
        });
      } catch (updateError) {
        console.error("Error updating failed payment status:", updateError);
      }

      throw new Error(`Failed to confirm payment: ${error.message}`);
    }
  }

  /**
   * Create Stripe Checkout Session
   */
  async createCheckoutSession(bookingData) {
    try {
      const {
        bookingId,
        amount,
        currency = "EUR",
        customerEmail,
      } = bookingData;

      // In production, this should call your Firebase Function
      // For now, we'll create a payment record
      const paymentRecord = {
        bookingId,
        amount,
        currency,
        customerEmail,
        paymentMethod: "stripe",
        status: "pending",
        createdAt: Timestamp.now(),
      };

      const paymentDoc = await addDoc(this.paymentsRef, paymentRecord);

      // Get Stripe instance if needed
      // const stripe = await stripePromise;

      // TODO: In production, call your Firebase Function to create checkout session
      // const response = await fetch('YOUR_FIREBASE_FUNCTION_URL/create-checkout-session', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ bookingId, amount, currency, customerEmail }),
      // });
      // const session = await response.json();
      // const result = await stripe.redirectToCheckout({ sessionId: session.id });

      return {
        paymentId: paymentDoc.id,
        // In production, you would redirect to Stripe Checkout here
        message:
          "Payment record created. Implement Firebase Function for Stripe Checkout.",
      };
    } catch (error) {
      console.error("Error creating checkout session:", error);
      throw error;
    }
  }

  /**
   * Create Payment Intent for custom payment flow
   */
  async createPaymentIntent(bookingData) {
    try {
      const {
        bookingId,
        amount,
        currency = "EUR",
        metadata = {},
      } = bookingData;

      // Create payment record
      const paymentRecord = {
        bookingId,
        amount,
        currency,
        status: "pending",
        metadata,
        createdAt: Timestamp.now(),
      };

      const paymentDoc = await addDoc(this.paymentsRef, paymentRecord);

      // TODO: Call Firebase Function to create payment intent
      // const response = await fetch('YOUR_FIREBASE_FUNCTION_URL/create-payment-intent', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ bookingId, amount, currency, metadata }),
      // });
      // const { clientSecret } = await response.json();

      return {
        paymentId: paymentDoc.id,
        // clientSecret: clientSecret, // This will come from your Firebase Function
        message: "Payment intent created. Implement Firebase Function.",
      };
    } catch (error) {
      console.error("Error creating payment intent:", error);
      throw error;
    }
  }

  /**
   * Confirm payment after successful Stripe payment
   */
  async confirmPayment(paymentId, stripePaymentId) {
    try {
      const paymentRef = doc(this.paymentsRef, paymentId);
      await updateDoc(paymentRef, {
        stripePaymentId,
        status: "succeeded",
        completedAt: Timestamp.now(),
      });

      return { success: true, paymentId };
    } catch (error) {
      console.error("Error confirming payment:", error);
      throw error;
    }
  }

  /**
   * Process refund
   */
  async processRefund(paymentId, amount = null) {
    try {
      // TODO: Call Firebase Function to process refund through Stripe
      // const response = await fetch('YOUR_FIREBASE_FUNCTION_URL/process-refund', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ paymentId, amount }),
      // });
      // const result = await response.json();

      const paymentRef = doc(this.paymentsRef, paymentId);
      await updateDoc(paymentRef, {
        status: "refunded",
        refundedAt: Timestamp.now(),
        refundAmount: amount,
      });

      return { success: true, message: "Refund processed" };
    } catch (error) {
      console.error("Error processing refund:", error);
      throw error;
    }
  }

  /**
   * Get Stripe instance
   */
  async getStripe() {
    return await stripePromise;
  }
}

const paymentService = new PaymentService();
export default paymentService;
